import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Bell, UserPlus, CalendarCheck, Users, CheckCheck } from 'lucide-react';
import { notificationsAPI, preferencesAPI, authAPI } from '../api/client';
import Layout from '../components/Layout';
import '../styles/notifications.css';

function timeAgo(iso) {
  if (!iso) return '';
  const mins = Math.floor((Date.now() - new Date(iso)) / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  return `${Math.floor(hrs / 24)}d ago`;
}

function iconForType(type) {
  switch (type) {
    case 'rsvp':               return { Icon: CalendarCheck, bg: '#e8f5fb', color: '#52AAD8' };
    case 'connection_request': return { Icon: UserPlus,     bg: '#e8f5fb', color: '#52AAD8' };
    case 'connection_accepted':return { Icon: UserPlus,     bg: '#e8f5fb', color: '#52AAD8' };
    case 'close_circle_invite':
    case 'close_circle_accepted': return { Icon: Users,    bg: '#fde8ef', color: '#D93868' };
    case 'group_invite':          return { Icon: Users,    bg: '#e8f5fb', color: '#52AAD8' };
    default:                   return { Icon: Bell,         bg: '#e8f5fb', color: '#52AAD8' };
  }
}

function Toggle({ on, onChange }) {
  return (
    <button className={`notif-toggle${on ? ' on' : ''}`} onClick={() => onChange(!on)} aria-pressed={on}>
      <span className="notif-toggle-thumb" />
    </button>
  );
}

export default function Notifications() {
  const navigate = useNavigate();
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [emailUnverified, setEmailUnverified] = useState(false);
  const [settings, setSettings] = useState({ invitations: true, comments: true, messages: false });
  const [settingsLoaded, setSettingsLoaded] = useState(false);
  useEffect(() => {
    notificationsAPI.getAll()
      .then(r => setNotifications(Array.isArray(r.data) ? r.data : []))
      .catch(() => {})
      .finally(() => setLoading(false));
    authAPI.getMe().then(r => {
      const me = r.data;
      const isEmailUser = me?.phone_email?.includes('@');
      if (isEmailUser && me.email_verified) {
        localStorage.setItem('emailVerified', '1');
        setEmailUnverified(false);
      } else if (isEmailUser && !me.email_verified && localStorage.getItem('emailVerified') !== '1') {
        setEmailUnverified(true);
      }
    }).catch(() => {});

    preferencesAPI.get().then(r => {
      const p = r.data;
      setSettings({
        invitations: !!p.notif_plan_invitations,
        comments:    !!p.notif_comments,
        messages:    !!p.notif_direct_messages,
      });
      setSettingsLoaded(true);
    }).catch(() => setSettingsLoaded(true));
  }, []);

  const markAllRead = async () => {
    await notificationsAPI.markAllRead().catch(() => {});
    setNotifications(prev => prev.map(n => ({ ...n, read_at: new Date().toISOString() })));
  };

  const markRead = async (id) => {
    await notificationsAPI.markRead(id).catch(() => {});
    setNotifications(prev => prev.map(n => n.id === id ? { ...n, read_at: new Date().toISOString() } : n));
  };

  const handleNotifClick = (n) => {
    if (!n.read_at) markRead(n.id);
    if (n.activity_id) navigate(`/activity/${n.activity_id}`);
    else if (n.notification_type === 'connection_request') navigate('/profile');
    else if (n.notification_type === 'connection_accepted') navigate('/profile');
    else if (n.notification_type?.startsWith('close_circle')) navigate('/close-circle');
    else if (n.notification_type === 'group_invite') navigate('/groups');
  };

  const toggle = (key) => {
    const next = { ...settings, [key]: !settings[key] };
    setSettings(next);
    preferencesAPI.update({
      notif_plan_invitations: next.invitations,
      notif_comments:         next.comments,
      notif_direct_messages:  next.messages,
    }).catch(() => {});
  };

  const unread = notifications.filter(n => !n.read_at);
  const read   = notifications.filter(n => n.read_at);

  return (
    <Layout title="Notifications">
      <div className="notif-page">

        {/* Recent Activity */}
        <section className="notif-section">
          <div className="notif-section-header">
            <h2 className="notif-section-title">
              Notifications
              {unread.length > 0 && <span className="notif-count-badge">{unread.length}</span>}
            </h2>
            {unread.length > 0 && (
              <button className="notif-mark-read" onClick={markAllRead}>
                <CheckCheck size={14} /> Mark all read
              </button>
            )}
          </div>

          {loading ? (
            <div className="notif-recent-card"><p className="notif-loading">Loading…</p></div>
          ) : notifications.length === 0 && !emailUnverified ? (
            <div className="notif-empty">
              <Bell size={32} color="#c3c5d9" />
              <p>You're all caught up!</p>
            </div>
          ) : (
            <div className="notif-recent-card">
              {emailUnverified && (
                <div className="notif-recent-row" style={{ cursor: 'pointer' }} onClick={() => navigate('/profile')}>
                  <div className="notif-recent-icon" style={{ background: '#fde8ef' }}>
                    <Bell size={16} color="#D93868" strokeWidth={2} />
                  </div>
                  <div className="notif-recent-content">
                    <div className="notif-recent-title-row">
                      <span className="notif-recent-title">Please verify your email address.</span>
                    </div>
                    <span className="notif-recent-meta">Go to Profile to resend the verification email →</span>
                  </div>
                </div>
              )}
              {unread.length > 0 && unread.map(n => {
                const { Icon, bg, color } = iconForType(n.notification_type);
                return (
                  <div key={n.id} className="notif-recent-row" onClick={() => handleNotifClick(n)} style={{ cursor: 'pointer' }}>
                    <div className="notif-recent-icon" style={{ background: bg }}>
                      <Icon size={16} color={color} strokeWidth={2} />
                    </div>
                    <div className="notif-recent-content">
                      <div className="notif-recent-title-row">
                        <span className="notif-recent-title">{n.content}</span>
                        <span className="notif-recent-time">{timeAgo(n.sent_at)}</span>
                      </div>
                    </div>
                    <div className="notif-unread-dot" />
                  </div>
                );
              })}
              {unread.length > 0 && read.length > 0 && (
                <div className="notif-divider"><span>Earlier</span></div>
              )}
              {read.map(n => {
                const { Icon, bg, color } = iconForType(n.notification_type);
                return (
                  <div key={n.id} className="notif-recent-row read" onClick={() => handleNotifClick(n)} style={{ cursor: 'pointer' }}>
                    <div className="notif-recent-icon" style={{ background: bg }}>
                      <Icon size={16} color={color} strokeWidth={2} />
                    </div>
                    <div className="notif-recent-content">
                      <div className="notif-recent-title-row">
                        <span className="notif-recent-title">{n.content}</span>
                        <span className="notif-recent-time">{timeAgo(n.sent_at)}</span>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </section>

        {/* Notification Settings */}
        <section className="notif-section">
          <div className="notif-section-header">
            <h2 className="notif-section-title">Notification Settings</h2>
          </div>
          <div className={`notif-recent-card${!settingsLoaded ? ' notif-settings-loading' : ''}`}>
            {[
              { key: 'invitations', label: 'Plan Invitations' },
              { key: 'comments',    label: 'Comments' },
              { key: 'messages',    label: 'Direct Messages' },
            ].map(({ key, label }) => (
              <div key={key} className="notif-recent-row" style={{ cursor: 'default' }}>
                <div className="notif-recent-content">
                  <div className="notif-recent-title-row">
                    <span className="notif-recent-title">{label}</span>
                  </div>
                </div>
                <Toggle on={settings[key]} onChange={() => toggle(key)} />
              </div>
            ))}
          </div>
        </section>

      </div>
    </Layout>
  );
}
